// src/pages/LoginPage.js
// Updated: 11/01/2025 09:16 PM EDT
// --- THIS IS THE FULL, UPDATED FILE ---

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth } from '../firebaseConfig';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { Vote, FileText, Award, LogIn } from 'lucide-react'; // Import icons

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Handle Email/Password Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      // The onAuthStateChanged listener in AuthContext will handle the redirect
      navigate('/dashboard');
    } catch (err) {
      setError('Failed to log in. Please check your email and password.');
      console.error('Login Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Note: Google Sign-In button is hidden for now
  // const handleGoogleSignIn = async () => { ... };

  return (
    <div className="flex min-h-screen flex-col bg-gray-100">
      {/* --- Header --- */}
      <header className="bg-white shadow-sm">
        <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-20 items-center justify-between">
            <div className="flex-shrink-0">
              <h1 className="text-3xl font-bold tracking-tight text-blue-700">
                BoardVote
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              {/* --- MODIFIED LINK --- */}
              <Link
                to="/register?flow=join"
                className="text-sm font-medium text-gray-600 hover:text-blue-600"
              >
                Join an Organization
              </Link>
              {/* --- END MODIFIED LINK --- */}
              <Link
                to="/register"
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
              >
                Create Organization
              </Link>
            </div>
          </div>
        </nav>
      </header>

      {/* --- Main Content --- */}
      <main className="flex-1">
        {/* Hero Section */}
        <div className="bg-white">
          <div className="mx-auto max-w-7xl py-16 px-4 sm:py-24 sm:px-6 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:px-8">
            {/* Hero Content */}
            <div className="flex flex-col justify-center">
              <h2 className="text-4xl font-extrabold text-gray-900 sm:text-5xl">
                Streamline Your Board's Decisions.
              </h2>
              <p className="mt-6 text-xl text-gray-600">
                BoardVote provides a secure, centralized platform for your
                organization's voting, document management, and package
                scoring. Stop juggling emails and spreadsheets.
              </p>
              <p className="mt-4 text-lg text-gray-600">
                Sign in to your account or create a new organization to get started.
              </p>
            </div>

            {/* Login Form */}
            <div className="mt-12 lg:mt-0">
              <div className="rounded-lg bg-white p-8 shadow-2xl">
                <h3 className="text-2xl font-bold text-gray-900">
                  Sign in to your account
                </h3>
                <form className="mt-6 space-y-6" onSubmit={handleLogin}>
                  {/* Email Input */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Email address
                    </label>
                    <div className="mt-1">
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  {/* Password Input */}
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Password
                    </label>
                    <div className="mt-1">
                      <input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  {/* Error Message Display */}
                  {error && <div className="text-sm text-red-600">{error}</div>}

                  {/* Submit Button */}
                  <div>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="flex w-full justify-center rounded-md border border-transparent bg-blue-600 py-3 px-4 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                    >
                      {isLoading ? (
                        'Signing in...'
                      ) : (
                        <>
                          <LogIn className="mr-2 h-5 w-5" /> Sign in
                        </>
                      )}
                    </button>
                  </div>
                </form>
                
                {/* Link to Register Page */}
                <div className="mt-6 text-center text-sm">
                  <p className="text-gray-600">
                    Need an account?{' '}
                    <Link
                      to="/register"
                      className="font-medium text-blue-600 hover:text-blue-500"
                    >
                      Create an organization
                    </Link>
                  </p>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="bg-gray-50 py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-blue-600">
                Features
              </h2>
              <p className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
                Everything your board needs, all in one place.
              </p>
            </div>
            <div className="mt-16 grid grid-cols-1 gap-12 md:grid-cols-3">
              {/* Feature 1 */}
              <div className="flex flex-col items-center text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-md bg-blue-600 text-white">
                  <Vote className="h-6 w-6" />
                </div>
                <h3 className="mt-6 text-xl font-bold text-gray-900">
                  Secure Voting
                </h3>
                <p className="mt-2 text-base text-gray-600">
                  Create named or secret ballots for motions. Restrict voting to
                  specific members and track results in real-time.
                </p>
              </div>
              {/* Feature 2 */}
              <div className="flex flex-col items-center text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-md bg-blue-600 text-white">
                  <FileText className="h-6 w-6" />
                </div>
                <h3 className="mt-6 text-xl font-bold text-gray-900">
                  Document Hub
                </h3>
                <p className="mt-2 text-base text-gray-600">
                  Upload, view, and comment on all your organization's
                  important documents, from meeting minutes to proposals.
                </p>
              </div>
              {/* Feature 3 */}
              <div className="flex flex-col items-center text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-md bg-blue-600 text-white">
                  <Award className="h-6 w-6" />
                </div>
                <h3 className="mt-6 text-xl font-bold text-gray-900">
                  Package Scoring
                </h3>
                <p className="mt-2 text-base text-gray-600">
                  Build custom, weighted rubrics to score proposals or
                  packages. Assign reviewers and see aggregated results.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* --- Footer --- */}
      <footer className="bg-gray-800 py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center text-gray-400">
          <p>
            &copy; {new Date().getFullYear()} Multi Customs LLC. All rights reserved.
          </p>
          <div className="mt-4 space-x-6">
            <Link to="/terms" className="text-sm hover:text-white">
              Terms of Conditions
            </Link>
            <span aria-hidden="true">&middot;</span>
            <Link to="/privacy" className="text-sm hover:text-white">
              Privacy Policy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
